/**
 * Represents a field configuration for a PS Table column.
 *
 * A PSTableField defines how a table column should be displayed, formatted, and behave.
 * Fields can be rendered using different modes: default text, custom formatters,
 * components, or ko-field-view components.
 *
 * @class PSTableField
 * @example
 * // Create a simple field with a string representing the field name
 * const field = new PSTableField('user_name');
 *
 * // Create a field with full options
 * const field = new PSTableField({
 *   field: 'created_date',
 *   name: 'created_date',
 *   label: 'Created Date',
 *   sortable: true,
 *   formatter: (value) => new Date(value).toLocaleDateString()
 * });
 *
 * // Create a field with ko-field-view component
 * const field = new PSTableField({
 *   field: 'status',
 *   type: 'select',
 *   options: { values: ['active', 'inactive', 'pending'] }
 * });
 */
export default class PSTableField
{
	/**
	 * Creates a new PSTableField instance.
	 *
	 * @param {string|Object} options - Field configuration options
	 * @param {string} [options.field] - The data field name to use as source for the field value
	 * @param {string} [options.name] - The display name for the field (defaults to field name). This is used to identify the field in the table.
	 * @param {string} [options.label] - The column header label
	 * @param {boolean} [options.sortable=true] - Whether the column is sortable
	 * @param {boolean} [options.visible=true] - Whether the column is visible
	 * @param {boolean} [options.locked=true] - Whether the column is locked in position in the field selector
	 * @param {boolean} [options.clickable=true] - Whether the column cells are clickable as part of the table row click handler
	 * @param {string} [options.style] - Inline CSS styles for the column
	 * @param {string} [options.classes] - CSS classes for the column
	 * @param {boolean} [options.auto_format_title=true] - Whether to auto-format the title. This is used to convert underscores to spaces and capitalize the first letter.
	 * @param {Function} [options.formatter] - Custom formatter function for the field value. This is used to format the field value before it is displayed in the table.
	 * @param {Object} [options.component] - Name of a component to use for rendering the field value. The component should expect its params to include value, row, field, and index.
	 * @param {string} [options.type='text'] - ko-field-view type for rendering. This is used to determine the type of field value to render.
	 * @param {Object} [options.options={}] - Options for ko-field-view, or specified component. This is used to pass additional options to the component or ko-field-view.
	 */
	constructor(options)
	{
		// Handle string input by converting to object with field and name properties
		if (typeof options === 'string')
			options = { field: options, name: options };

		options = options ?? {};

		this.field = ko_helper.safe_observable(options.field, '');
		this.name = ko_helper.safe_observable(options.name, this.field() ?? null);
		this.label = ko_helper.safe_observable(options.label, null);
		this.sortable = ko_helper.safe_observable(options.sortable, true);
		this.visible = ko_helper.safe_observable(options.visible, true);
		this.locked = ko_helper.safe_observable(options.locked, false);
		this.clickable = ko_helper.safe_observable(options.clickable, true);
		this.style = ko_helper.safe_observable(options.style, {});
		this.classes = ko_helper.safe_observable(options.classes, null);
		this.auto_format_title = ko_helper.safe_observable(options.auto_format_title, true);
		this.formatter = ko_helper.safe_observable(options.formatter, null);
		this.component = ko_helper.safe_observable(options.component, null);
		this.on_click = options.on_click || null;

		// ko-field-view support
		this.type = ko_helper.safe_observable(options.type, 'text');
		this.options = ko_helper.safe_observable(options.options, {});

		/**
		 * Computed observable that provides the column title.
		 * Uses label if available, otherwise formats the name, or field name.
		 * @type {ko.PureComputed<string>}
		 */
		this.title = ko.pureComputed(() =>
		{
			let title = this.label();
			if (!(title?.length > 0))
				title = this.formatTitle(this.name() ?? this.field());

			return title;
		});

		/**
		 * Computed observable that determines the rendering mode.
		 * Priority: formatter > component > ko-field-view > default.
		 * @type {ko.PureComputed<string>}
		 */
		this.render_mode = ko.pureComputed(() =>
		{
			if (this.formatter())
				return 'formatter';
			else if (this.component())
				return 'component';
			else if (this.type())
				return 'ko-field-view';
			else
				return 'default';
		});
	}

	/**
	 * Formats a title string by converting underscores to spaces and capitalizing the first letter. Only applicable if
	 * auto_format_title is true and no label is provided.
	 *
	 * @param {string|Function} title - The title to format, can be a string or function
	 * @returns {string} The formatted title
	 */
	formatTitle(title)
	{
		if (title instanceof Function)
			title = title();

		if (this.auto_format_title())
		{
			title = title.replace(/_/g, ' ');
			title = title.trim();
			title = title.charAt(0).toUpperCase() + title.slice(1);
		}

		return title;
	}

	/**
	 * Formats a field value using the configured formatter function. If no formatter is provided, the value is returned as is.
	 *
	 * @param {any} value - The raw value to format
	 * @param {Object} row - The entire row data object
	 * @returns {any} The formatted value
	 */
	formatValue(value, row)
	{
		let formatter = this.formatter();
		if (formatter instanceof Function)
		{
			value = formatter(value, row);
		}
		// TODO: Add support for formatters from grape registry, or is it better to just use the ko-field-view rendering mode?

		return value;
	}
}
